import { useChat } from "@ai-sdk/react";
import { ArrowUpOutlined, CloseOutlined, FileTextOutlined, LoadingOutlined, PaperClipOutlined, QuestionCircleOutlined, ReloadOutlined, StopOutlined } from "@ant-design/icons";
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from "ai";
import { fetchAuthSession } from "aws-amplify/auth";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useHistory, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { parseCsvFile, type ParsedCsv } from "./csv";
import { extractSchemaContext, extractSchemaListContext } from "./helpers";
import { tryParseStreamError } from "./streamError";
import { KNOWN_TOOL_PART_TYPES, renderToolPart } from "./tools";
import type { SchemaListItem, ToolHelpers } from "./tools/types";

// The deployed copilot Lambda requires a Cognito JWT in the Authorization
// header, same as the rest of the app's API calls. Resolved fresh per
// request so we don't ship an expired token.
async function authHeaders(): Promise<Record<string, string>> {
    try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

interface SchemaAssistantContentProps {
    manageSchemaModelRef: React.RefObject<any>;
}

const COPILOT_API_BASE = process.env.REACT_APP_COPILOT_API || "https://24m7bef4jqvsp7pxnp5orbgm540npqzi.lambda-url.us-east-2.on.aws";
const ASSISTANT_SCHEMAS_URL = `${COPILOT_API_BASE.replace(/\/+$/, "")}/assistant/schemas`;

export default function SchemaAssistantContent({ manageSchemaModelRef }: SchemaAssistantContentProps) {
    const controllerRef = manageSchemaModelRef;
    const [input, setInputText] = useState("");
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [attachedCsv, setAttachedCsv] = useState<ParsedCsv | null>(null);
    const [csvBusy, setCsvBusy] = useState(false);
    const [csvError, setCsvError] = useState<string | null>(null);
    const lastSeenCreatedSchemaIdRef = useRef<string | null>(null);

    const dispatch = useAppDispatch();
    const history = useHistory();
    const urlParams = useParams<{ tenant?: string; businessarea?: string }>();

    const userBusinessAreas = useAppSelector((s: any) => s.schemaModels?.userBusinessAreas);
    const schemaList: SchemaListItem[] = useAppSelector((s: any) => s.schemaModels?.schemaModelListResult?.Items || []);
    const createSchemaModelResult = useAppSelector((s: any) => s.schemaModels?.createSchemaModelResult);
    const createdSchemaModel = useAppSelector((s: any) => s.schemaModels?.createdSchemaModel);
    const updateSchemaModelResult = useAppSelector((s: any) => s.schemaModels?.updateSchemaModelResult);
    const deleteSchemaModelResult = useAppSelector((s: any) => s.schemaModels?.deleteSchemaModelResult);
    const tenantId: string | undefined = useAppSelector((s: any) => s.userLogin?.tenant?.tenantId);

    const businessAreaId = useMemo(() => {
        const ba = urlParams.businessarea ? userBusinessAreas?.[urlParams.businessarea.toLowerCase()] : undefined;
        return ba?.businessAreaId;
    }, [urlParams.businessarea, userBusinessAreas]);

    // useChat caches the transport from its first render; later updates to the
    // memoized transport object don't get re-bound. We capture the live state
    // (attached CSV) into a ref so prepareSendMessagesRequest always reads the
    // current value at call time.
    const attachedCsvRef = useRef<ParsedCsv | null>(null);
    useEffect(() => {
        attachedCsvRef.current = attachedCsv;
    }, [attachedCsv]);

    const schemaListRef = useRef<SchemaListItem[]>(schemaList);
    useEffect(() => {
        schemaListRef.current = schemaList;
    }, [schemaList]);

    const transport = useMemo(
        () =>
            new DefaultChatTransport({
                api: ASSISTANT_SCHEMAS_URL,
                headers: authHeaders,
                prepareSendMessagesRequest: ({ messages, body }) => {
                    const csv = attachedCsvRef.current;
                    return {
                        body: {
                            ...(body || {}),
                            messages,
                            schema: extractSchemaContext(controllerRef?.current),
                            schemaList: extractSchemaListContext(schemaListRef.current),
                            csv: csv
                                ? {
                                      fileName: csv.fileName,
                                      rowCount: csv.rowCount,
                                      columnCount: csv.columnCount,
                                      sampledRowCount: csv.sampledRowCount,
                                      sampledColumnCount: csv.sampledColumnCount,
                                      truncated: csv.truncated,
                                      sampleRows: csv.sampleRows,
                                  }
                                : undefined,
                        },
                    };
                },
            }),
        [controllerRef],
    );

    const { messages, sendMessage, status, stop, error, clearError, regenerate, addToolOutput } = useChat({
        transport,
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    });

    const helpers: ToolHelpers = useMemo(
        () => ({
            addToolOutput: (args) => {
                addToolOutput(args as any);
            },
            dispatch,
            history,
            urlParams,
            tenantId,
            businessAreaId,
            schemaList,
            createSchemaModelResult,
            createdSchemaModel,
            updateSchemaModelResult,
            deleteSchemaModelResult,
        }),
        [
            addToolOutput,
            dispatch,
            history,
            urlParams,
            tenantId,
            businessAreaId,
            schemaList,
            createSchemaModelResult,
            createdSchemaModel,
            updateSchemaModelResult,
            deleteSchemaModelResult,
        ],
    );

    const isBusy = status === "submitted" || status === "streaming";

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, status]);

    // Auto-clear the attached CSV after a successful createSchema round-trip.
    useEffect(() => {
        const newId = createdSchemaModel?.schemaId;
        if (!newId) return;
        if (newId === lastSeenCreatedSchemaIdRef.current) return;
        lastSeenCreatedSchemaIdRef.current = newId;
        if (attachedCsv) {
            setAttachedCsv(null);
            setCsvError(null);
        }
    }, [createdSchemaModel, attachedCsv]);

    const handleSend = () => {
        const text = input.trim();
        if (!text || isBusy) return;
        clearError?.();
        sendMessage({ text });
        setInputText("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handlePickFile = () => {
        if (csvBusy || isBusy) return;
        fileInputRef.current?.click();
    };

    // One-click retry for a failed turn (e.g. a 429 rate-limit that survived the
    // backend's auto-retries). Clears the stale error banner and re-runs the last
    // user turn via useChat's regenerate() — the user does not have to retype.
    const handleRetry = () => {
        if (isBusy) return;
        clearError?.();
        void regenerate();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // reset value so the same file can be re-picked
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file) return;
        if (!/\.csv$/i.test(file.name)) {
            setCsvError("Only .csv files are supported.");
            return;
        }
        setCsvBusy(true);
        setCsvError(null);
        try {
            const parsed = await parseCsvFile(file);
            if (parsed.rowCount === 0 && parsed.columnCount === 0) {
                setCsvError("That file looks empty.");
                setAttachedCsv(null);
            } else {
                setAttachedCsv(parsed);
            }
        } catch (err: any) {
            setCsvError(err?.message || "Couldn't parse that CSV.");
            setAttachedCsv(null);
        } finally {
            setCsvBusy(false);
        }
    };

    const handleClearCsv = () => {
        setAttachedCsv(null);
        setCsvError(null);
    };

    return (
        <div className="flex h-full min-h-0 flex-col bg-[#fafafa] text-zinc-900">
            <div className="flex flex-none items-center justify-center px-4 py-3">
                <p className="m-0 text-center text-xs text-zinc-500">Responses are generated using AI and may contain mistakes.</p>
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
                {messages.map((m) => {
                    if (m.role === "user") {
                        const text = Array.isArray(m.parts)
                            ? m.parts
                                  .filter((p: any) => p?.type === "text")
                                  .map((p: any) => p.text || "")
                                  .join("")
                            : "";
                        if (!text) return null;
                        return (
                            <div key={m.id} className="flex justify-end">
                                <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-[#001D3D] px-3 py-2 text-sm text-white">{text}</div>
                            </div>
                        );
                    }
                    const parts = Array.isArray(m.parts) ? m.parts : [];
                    return (
                        <div key={m.id} className="flex flex-col gap-2">
                            {parts.map((part: any, idx: number) => {
                                if (part.type === "text") {
                                    if (!part.text) return null;
                                    // Defensive: if the AI SDK leaked a provider error frame as plain text,
                                    // render it as a friendly red banner instead of as a chat bubble.
                                    const parsedErr = tryParseStreamError(part.text);
                                    if (parsedErr) {
                                        return (
                                            <div key={idx} className="flex justify-start">
                                                <div className="flex max-w-[90%] flex-col gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                                    <span>{parsedErr.friendly}</span>
                                                    <button
                                                        type="button"
                                                        onClick={handleRetry}
                                                        disabled={isBusy}
                                                        className="flex items-center gap-1.5 self-start rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        <ReloadOutlined spin={isBusy} /> Try again
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={idx} className="flex justify-start">
                                            <div className="max-w-[90%] rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-800">
                                                <div className="schema-assistant-markdown">
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.text}</ReactMarkdown>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                if (KNOWN_TOOL_PART_TYPES.has(part.type)) {
                                    return (
                                        <div key={idx} className="flex justify-start">
                                            <div className="w-full max-w-[95%]">{renderToolPart(part, helpers)}</div>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    );
                })}
                {status === "submitted" && (
                    <div className="flex justify-start">
                        <div className="rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-500">
                            <LoadingOutlined spin /> Thinking...
                        </div>
                    </div>
                )}
                {error && (
                    <div className="flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        <span>{error.message || "Something went wrong."}</span>
                        <button
                            type="button"
                            onClick={handleRetry}
                            disabled={isBusy}
                            className="flex items-center gap-1.5 self-start rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ReloadOutlined spin={isBusy} /> Try again
                        </button>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="flex flex-none flex-col gap-2 border-t border-zinc-200 bg-[#fafafa] px-4 py-3">
                <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileChange} />
                {attachedCsv && (
                    <div className="rounded-md border border-zinc-200 bg-white p-2 text-xs text-zinc-700">
                        <div className="flex items-center gap-2">
                            <FileTextOutlined className="text-[#001D3D]" />
                            <span className="truncate font-medium text-zinc-900">{attachedCsv.fileName}</span>
                            <span className="text-zinc-500">
                                · {attachedCsv.rowCount.toLocaleString()} row{attachedCsv.rowCount === 1 ? "" : "s"} · {attachedCsv.columnCount} col
                                {attachedCsv.columnCount === 1 ? "" : "s"}
                            </span>
                            <div className="flex-1" />
                            <button
                                type="button"
                                onClick={handleClearCsv}
                                className="rounded-full p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                                title="Remove attachment"
                            >
                                <CloseOutlined />
                            </button>
                        </div>
                        {(attachedCsv.truncated.rows || attachedCsv.truncated.columns) && (
                            <div className="mt-1 text-[10px] text-zinc-500">
                                Sampling {attachedCsv.sampledRowCount} of {attachedCsv.rowCount.toLocaleString()} rows
                                {attachedCsv.truncated.columns && ` and ${attachedCsv.sampledColumnCount} of ${attachedCsv.columnCount} columns`}.
                            </div>
                        )}
                        {attachedCsv.parseErrors.length > 0 && <div className="mt-1 text-[10px] text-amber-700">{attachedCsv.parseErrors[0]}</div>}
                        {attachedCsv.sampleRows.length > 0 && (
                            <div className="mt-2 overflow-x-auto rounded-md border border-zinc-200">
                                <table className="min-w-full border-collapse text-left text-[10px]">
                                    <tbody>
                                        {attachedCsv.sampleRows.slice(0, 3).map((row, ri) => (
                                            <tr key={ri} className="border-t border-zinc-200 first:border-t-0">
                                                {row.slice(0, 6).map((v, ci) => (
                                                    <td key={ci} className="max-w-[140px] truncate px-2 py-1 text-zinc-800">
                                                        {v === null || v === undefined ? <span className="text-zinc-400">∅</span> : String(v)}
                                                    </td>
                                                ))}
                                                {row.length > 6 && <td className="px-2 py-1 text-zinc-400">…</td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                {csvError && !attachedCsv && <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">{csvError}</div>}
                <div className="rounded-2xl border border-[#001D3D]/40 bg-white px-3 py-2 focus-within:border-[#001D3D]">
                    <textarea
                        value={input}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={attachedCsv ? "What should I do with this file?" : "Ask a question..."}
                        rows={2}
                        className="w-full resize-none border-0 bg-transparent text-sm text-zinc-900 placeholder-zinc-400 outline-none"
                        disabled={status === "error"}
                    />
                    <div className="mt-1 flex items-center justify-between">
                        <button
                            type="button"
                            onClick={handlePickFile}
                            disabled={csvBusy || isBusy}
                            className="rounded-full p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                            title={attachedCsv ? "Replace CSV" : "Attach a CSV"}
                        >
                            {csvBusy ? <LoadingOutlined spin /> : <PaperClipOutlined />}
                        </button>
                        {isBusy ? (
                            <button
                                type="button"
                                onClick={() => stop()}
                                className="flex h-7 w-7 items-center justify-center rounded-full bg-[#001D3D] text-white hover:bg-[#002D5C]"
                                title="Stop"
                            >
                                <StopOutlined />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={!input.trim()}
                                className={`flex h-7 w-7 items-center justify-center rounded-full ${
                                    input.trim() ? "bg-[#001D3D] text-white hover:bg-[#002D5C]" : "cursor-not-allowed bg-zinc-200 text-zinc-400"
                                }`}
                                title="Send"
                            >
                                <ArrowUpOutlined />
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <QuestionCircleOutlined />
                    <a href="mailto:support@pretectum.com" className="text-zinc-600 hover:text-zinc-900">
                        Contact support
                    </a>
                </div>
            </div>
        </div>
    );
}
